import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const itemTypes = [
    { name: 'Phone', icon: '📱' },
    { name: 'Wallet', icon: '👛' },
    { name: 'Keys', icon: '🔑' },
    { name: 'Jewelry', icon: '💍' },
    { name: 'Watch', icon: '⌚' },
    { name: 'Sunglasses', icon: '🕶️' },
    { name: 'Headphones', icon: '🎧' },
    { name: 'Charger', icon: '🔌' },
    { name: 'Laptop', icon: '💻' },
    { name: 'Tablet', icon: '📱' },
    { name: 'Camera', icon: '📷' },
    { name: 'Bag', icon: '👜' },
    { name: 'Backpack', icon: '🎒' },
    { name: 'Jacket', icon: '🧥' },
    { name: 'Shoes', icon: '👟' },
    { name: 'Hat', icon: '👒' },
    { name: 'Scarf', icon: '🧣' },
    { name: 'Umbrella', icon: '☂️' },
    { name: 'Book', icon: '📚' },
    { name: 'Documents', icon: '📄' },
    { name: 'Passport', icon: '📘' },
    { name: 'Credit Card', icon: '💳' },
    { name: 'Medication', icon: '💊' },
    { name: 'Toiletries', icon: '🧴' },
];

export default function ItemTypeDropdown({ value, onChange, placeholder }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState(value || '');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownSearchRef = useRef(null);

    // Get localized name for an item type
    const getLocalizedName = (itemName) => {
        return t(`itemTypes.${itemName.toLowerCase().replace(' ', '_')}`, { defaultValue: itemName });
    };

    // Get default placeholder text
    const defaultPlaceholder = t('itemTypes.selectItemType', { defaultValue: 'Select item type' });

    // Find selected item type by name
    const selectedItemType = itemTypes.find(item =>
        item.name.toLowerCase() === value?.toLowerCase()
    );

    // Update input value when prop value changes
    useEffect(() => {
        if (value && selectedItemType) {
            setInputValue(getLocalizedName(selectedItemType.name));
        } else {
            setInputValue(value || '');
        }
    }, [value, t]);

    // Filter item types based on search term (search both English and localized names)
    const filteredItemTypes = itemTypes.filter(item => {
        const englishName = item.name.toLowerCase();
        const localizedName = getLocalizedName(item.name).toLowerCase();
        const search = searchTerm.toLowerCase();
        return englishName.includes(search) || localizedName.includes(search);
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus dropdown search input when dropdown opens
    useEffect(() => {
        if (isOpen && dropdownSearchRef.current) {
            dropdownSearchRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (itemType) => {
        setInputValue(getLocalizedName(itemType.name));
        onChange(itemType.name); // Still pass English name to maintain DB consistency
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        
        // Try to find matching item type (check both English and localized names)
        const matchingType = itemTypes.find(item => 
            item.name.toLowerCase() === newValue.toLowerCase() ||
            getLocalizedName(item.name).toLowerCase() === newValue.toLowerCase()
        );
        
        if (matchingType) {
            onChange(matchingType.name); // Pass English name for DB consistency
        } else {
            onChange(newValue); // Pass the typed value if no match found
        }
    };

    const handleInputFocus = () => {
        // Don't open dropdown when focusing on input for typing
    };

    const handleDropdownToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        } else if (e.key === 'ArrowDown' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
        }
    };

    const handleDropdownKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            inputRef.current?.focus();
        }
    };

    return (
        <div className="item-type-dropdown" ref={dropdownRef}>
            <div className="item-type-input-container">
                {selectedItemType && (
                    <span className="item-type-icon">{selectedItemType.icon}</span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    className="item-type-input-field"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || defaultPlaceholder}
                />
                <button
                    type="button"
                    className="dropdown-toggle-button"
                    onClick={handleDropdownToggle}
                    tabIndex={-1}
                >
                    <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
                </button>
            </div>

            {isOpen && (
                <div className="item-type-dropdown-list">
                    <input
                        ref={dropdownSearchRef}
                        type="text"
                        className="item-type-dropdown-search"
                        placeholder={t('itemTypes.searchPlaceholder', { defaultValue: 'Search item types...' })}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleDropdownKeyDown}
                    />
                    {filteredItemTypes.length > 0 ? (
                        filteredItemTypes.map((itemType, index) => (
                            <div
                                key={`${itemType.name}-${index}`}
                                className="item-type-dropdown-item"
                                onClick={() => handleSelect(itemType)}
                            >
                                <span className="item-type-icon">{itemType.icon}</span>
                                <span className="item-type-name">{getLocalizedName(itemType.name)}</span>
                            </div>
                        ))
                    ) : (
                        <div className="item-type-dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                            {t('itemTypes.noItemsFound', { defaultValue: 'No item types found' })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
} 